import RootStore from '@App/stores/root-store'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { PASSWORD_REGEX } from '@Const/regexp'
import { PHONE_NUMBER_FORM } from '@Const/forms-constants'
import { UserInfo } from '@Entities/user/user'
import { activateUser, createUserPassword } from '@App/api/routes/user'
import { CreatePasswordStatus } from '@App/enum/create-password-status'
import { HttpError } from '@App/exceptions/HttpError'

interface PhoneNumberValidation {
    phoneCountryCode: { error: string }
    fullPhoneNumber: { error: string }
}

export default class CreatePasswordStore {
    private readonly rootStore: RootStore
    private readonly _phoneNumberValidation: PhoneNumberValidation

    private _user: UserInfo
    private _password: string
    private _passwordError: string
    private _password2: string
    private _passwordError2: string
    private _codeError: string
    private _code: string
    private _phoneCountryCode: string
    private _fullPhoneNumber: string
    private _createPasswordStatus: CreatePasswordStatus
    private _isEmailMFA: boolean
    private _enrollMfaEmail: string | null
    private _isConfigMFAValid: boolean

    constructor(rootStore: RootStore) {
        makeObservable<
            CreatePasswordStore,
            | '_user'
            | '_password'
            | '_passwordError'
            | '_password2'
            | '_passwordError2'
            | '_codeError'
            | '_enrollMfaEmail'
            | '_code'
            | '_phoneCountryCode'
            | '_fullPhoneNumber'
            | '_phoneNumberValidation'
            | '_createPasswordStatus'
            | '_isEmailMFA'
            | '_isConfigMFAValid'
        >(this, {
            _user: observable,
            _password: observable,
            _passwordError: observable,
            _password2: observable,
            _passwordError2: observable,
            _codeError: observable,
            _code: observable,
            _enrollMfaEmail: observable,
            _phoneCountryCode: observable,
            _fullPhoneNumber: observable,
            _phoneNumberValidation: observable,
            _createPasswordStatus: observable,
            _isEmailMFA: observable,
            _isConfigMFAValid: observable,
            email: computed,
            isPasswordValid: computed,
            isConfigMFAValid: computed,
            formattedPhoneNumber: computed,
            activateUser: action,
            createPassword: action,
            getValue: action,
            setEnrollMfaEmail: action,
            submitConfigurationMFA: action,
            submitMfaCode: action,
            setIsConfigMFAValid: action,
            validatorConfigMFA: action,
        })
        this.rootStore = rootStore
        this._createPasswordStatus = CreatePasswordStatus.setting_password
        this._isEmailMFA = false
        this._isConfigMFAValid = false
        this._phoneNumberValidation = {
            phoneCountryCode: { error: '' },
            fullPhoneNumber: { error: '' },
        }
    }

    get password(): string {
        return this._password
    }

    get phoneCountryCode(): string {
        return this._phoneCountryCode
    }

    get fullPhoneNumber(): string {
        return this._fullPhoneNumber
    }

    get passwordError(): string {
        return this._passwordError
    }

    get passwordError2(): string {
        return this._passwordError2
    }

    get isEmailMFA(): boolean {
        return this._isEmailMFA
    }

    get code(): string {
        return this._code
    }

    get codeError(): string {
        return this._codeError
    }

    get email(): string {
        return this._enrollMfaEmail ?? this._user?.email
    }

    get formattedPhoneNumber(): string {
        return `+${this.phoneCountryCode}${this.fullPhoneNumber}`
    }

    get createPasswordStatus(): CreatePasswordStatus {
        return this._createPasswordStatus
    }

    get phoneNumberValidation(): PhoneNumberValidation {
        return this._phoneNumberValidation
    }

    get isPasswordValid(): boolean {
        return !!(
            this._password &&
            this._password2 &&
            !this._passwordError &&
            !this._passwordError2 &&
            this._password === this._password2
        )
    }

    get isConfigMFAValid(): boolean {
        return this._isConfigMFAValid
    }

    setEnrollMfaEmail(email: string): void {
        this._enrollMfaEmail = email
    }
    public setIsConfigMFAValid(isConfigMFAValid: boolean) {
        this._isConfigMFAValid = isConfigMFAValid
    }

    public validatorConfigMFA(): void {
        const isPhoneNumberValid = !(
            this.phoneNumberValidation.fullPhoneNumber.error || this.phoneNumberValidation.phoneCountryCode.error
        )
        this._isConfigMFAValid = !!(this.fullPhoneNumber && isPhoneNumberValid)
    }

    public setIsMFAType(isMFAType: boolean) {
        this._isEmailMFA = isMFAType
    }

    public async createPassword(activatingCode: string) {
        try {
            await createUserPassword(activatingCode, this._password, this._phoneCountryCode, this._fullPhoneNumber)

            this._createPasswordStatus = CreatePasswordStatus.configuration_mfa
        } catch (error) {
            if (error instanceof HttpError) {
                this.rootStore.popupStore.setAlertData(error.message, true)
            }
        }
    }

    public async activateUser(activatingCode: string) {
        const response = await activateUser(activatingCode)
        const data = response.data

        if (data.error) {
            throw new Error(data.message)
        }
        if (data.email) {
            runInAction(() => (this._user = data))
            this.getValue(
                this._user.phoneCountryCode ? this._user.phoneCountryCode.toString(10) : '',
                PHONE_NUMBER_FORM.phoneCountryCode.id
            )
            this.getValue(this._user.fullPhoneNumber ? this._user.fullPhoneNumber : '', 'fullPhoneNumber')
        }
    }

    public async submitConfigurationMFA() {
        const reply = await this.rootStore.userStore.enrollUser2fa(
            !this._isEmailMFA,
            this.email,
            this._password,
            this._phoneCountryCode,
            this._fullPhoneNumber
        )
        if (this._isEmailMFA) {
            this._createPasswordStatus = CreatePasswordStatus.config_done
        } else {
            if (reply === 'no_update') {
                this._createPasswordStatus = CreatePasswordStatus.config_done
            } else {
                this._createPasswordStatus = CreatePasswordStatus.confirm_sms_code
            }
        }
    }

    public async submitMfaCode() {
        await this.rootStore.userStore.submitPhoneEnrollmentMfaCode(this._code)
        this._createPasswordStatus = CreatePasswordStatus.config_done
    }

    public getValue = (value: string, id: string) => {
        switch (id) {
            case 'password':
                this._passwordError = value.match(PASSWORD_REGEX)
                    ? ''
                    : !value.length
                      ? 'Required'
                      : 'Minimum 10 characters and at least one upper case, one lower case, ' +
                        'one number and one special character'
                this._passwordError2 = this._password2 && this._password2 !== value ? 'Passwords must match' : ''
                this._password = value
                break
            case 'password2':
                this._passwordError2 = !value.length
                    ? 'Required'
                    : value !== this._password
                      ? 'Passwords must match'
                      : ''
                this._password2 = value
                break
            case 'code':
                this._codeError = !value.length ? 'Required' : ''
                this._code = value
                break
            case 'email':
                this.setEnrollMfaEmail(value)
                break
            case PHONE_NUMBER_FORM.phoneCountryCode.id:
                this._phoneNumberValidation.phoneCountryCode.error = value.length <= 0 ? 'Country Code is required' : ''
                this._phoneCountryCode = value
                break
            case 'fullPhoneNumber':
                this._phoneNumberValidation.fullPhoneNumber.error = !value.length ? 'Phone number is required' : ''
                this._fullPhoneNumber = value
                break
            default:
                break
        }
    }
}
